"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";

interface FeatureCardsProps {
  className?: string;
}

export default function FeatureCards({ className }: FeatureCardsProps) {
  const dict = useDictionary();

  const features = [
    {
      iconSrc: "/icon/fb.png",
      title: dict.featureCards.metaTitle,
      description: dict.featureCards.metaDesc,
    },
    {
      iconSrc: "/icon/gads.png",
      title: dict.featureCards.googleTitle,
      description: "Google Analytics, Google Ads, Google Search Console",
    },
    {
      iconSrc: "/icon/line.png",
      title: dict.featureCards.lineTitle,
      description: "LINE Ads Platform",
    },
    {
      iconSrc: "/icon/shopify.png",
      title: dict.featureCards.ecommerceTitle,
      description: dict.featureCards.ecommerceDesc,
    },
  ];

  return (
    <section className={cn("py-20 bg-[var(--darker-blue)]", className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)]">
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
              {dict.featureCards.heading}
            </span>
            <motion.span className="hero-ink text-6xl font-black sm:text-7xl lg:text-7xl">
              3
            </motion.span>
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
              {dict.featureCards.headingUnit}
            </span>
          </h2>
        </motion.div>

        {/* Feature Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -5 }}
              className="group relative flex h-full min-h-[200px] flex-col overflow-hidden rounded-[1.75rem] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(232,242,255,0.92)_100%)] p-5 shadow-[0_20px_50px_rgba(148,163,184,0.12)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(59,130,246,0.16)] sm:p-6"
              >
                <div
                  className={cn(
                    "w-14 h-14 rounded-2xl bg-gradient-to-br flex items-center justify-center group-hover:scale-110 transition-transform duration-300",
                    feature,
                  )}
                >
                  <Image
                    src={feature.iconSrc}
                    alt={feature.title}
                    width={35}
                    height={35}
                    className="w-7 h-7 object-contain"
                  />
                </div>

                <h3 className="text-lg font-bold text-[var(--blue-900)] mb-2 group-hover:text-[var(--blue-700)] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
